import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
} from '@/components/ui/form';
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
} from '@/components/ui/select';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

import { Input } from '@/components/ui/input';
import { z } from 'zod';
import { useForm, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  Edit2,
  FileText,
  Image,
  ImageIcon,
  Loader2,
  Plus,
  Trash2,
  Upload,
  X,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import createApiClient from '@/api/axiosInstance';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Label } from '@/components/ui/label';
import {
  clearDumpAction,
  partCreationDump,
} from '@/js/reduxstore/actions/dumpActions';
import CADFileUploader from '@/js/components/upload/CADFileUploader';
import {
  fetchExistingDocuments,
  fetchExistingDocumentsArray,
} from '@/js/components/FileUploadBox';
import FileUpload from '@/js/components/FileUpload';

const FORM_STATE_ENUM = {
  CREATING_PART: 'CREATING_PART',
  DISPLAY_PART: 'DISPLAY_PART',
  DISPLAY_PART_RFQ: 'DISPLAY_PART_RFQ',
  EDIT_FORM: 'EDIT_FORM',
};

const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100MB
const ACCEPTED_FILE_TYPES_IMAGE = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
];
const ACCEPTED_FILE_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/svg+xml',
];

const materialItemSchema = z.object({
  material_category: z.string().min(1, 'Required'),
  required_material: z.string().min(1, 'Required'),
  material_standard: z.string().min(1, 'Required'),
  material_compliance: z.string().min(1, 'Required'),
  manufacturing_process: z.string().min(1, 'Required'),
  surface_finish_process: z.string().min(1, 'Required'),
  heat_treatment_process: z.string().min(1, 'Required'),
  document_ids: z.array(z.any()).optional(), // IDs of uploaded documents
});

const testMethodSchema = z.object({
  test_method_name: z.string().min(1, 'Required'),
  certification_required: z.boolean(),
});

const formSchema = z.object({
  part_name: z.string().min(1, 'Required'),
  part_number: z.string().min(1, 'Required'),
  part_revision: z.preprocess(
    (val) => (val === '' ? undefined : Number(val)),
    z
      .number({ invalid_type_error: 'Required' })
      .positive('Must be greater than 0'),
  ),
  life_cycle_state: z.string().optional(),
  part_owner: z.string().optional(),
  description: z.string().min(1, 'Required'),
  usage_per_product: z.preprocess(
    (val) => (val === '' ? undefined : Number(val)),
    z
      .number({ invalid_type_error: 'Required' })
      .positive('Must be greater than 0'),
  ),
  total_parts_required: z.preprocess(
    (val) => (val === '' ? undefined : Number(val)),
    z
      .number({ invalid_type_error: 'Required' })
      .positive('Must be greater than 0'),
  ),
  precision: z.preprocess(
    (val) => (val === '' ? undefined : Number(val)),
    z
      .number({ invalid_type_error: 'Required' })
      .positive('Must be greater than 0'),
  ),
  precision_uom: z.string().min(1, 'Required'),
  length: z.preprocess(
    (val) => (val === '' ? undefined : Number(val)),
    z
      .number({ invalid_type_error: 'Required' })
      .positive('Must be greater than 0'),
  ),
  width: z.preprocess(
    (val) => (val === '' ? undefined : Number(val)),
    z
      .number({ invalid_type_error: 'Required' })
      .positive('Must be greater than 0'),
  ),
  height: z.preprocess(
    (val) => (val === '' ? undefined : Number(val)),
    z
      .number({ invalid_type_error: 'Required' })
      .positive('Must be greater than 0'),
  ),
  dimension_uom: z.string().min(1, 'Required'),
  tolerance: z.preprocess(
    (val) => (val === '' ? undefined : Number(val)),
    z
      .number({ invalid_type_error: 'Required' })
      .positive('Must be greater than 0'),
  ),
  type_of_uom: z.string().min(1, 'Required'),
  tolerance_uom: z.string().min(1, 'Required'),
  estimated_weight: z.preprocess(
    (val) => (val === '' ? undefined : Number(val)),
    z
      .number({ invalid_type_error: 'Required' })
      .positive('Must be greater than 0'),
  ),
  weight_uom: z.string().min(1, 'Required'),
  material_manufacturing_info: z
    .array(z.any())
    .min(1, 'At least one material & manufacturing info is required'),
  test_methods: z
    .array(z.any())
    .min(1, 'At least one testing method is required'),
  additional_note: z.string().optional(),
});

const UOM_OPTIONS = {
  length: ['micron', 'mm', 'cm', 'inche'],
  volume: ['mm³', 'cm³'],
  liquid_volume: ['mL', 'L'],
  weight: ['mg', 'gm', 'kg'],
};

const PartDetailsScreen2 = ({
  selectedPart,
  onClose,
  onSubmit,
  state,
  totalQuantityToManufacture,
}) => {
  // TODO:
  // WORK ON TOTAL PARTS REQUIRED FIELD
  // GET ALL THE LIST OF UOMS
  // CAD FILE UPLOAD PROBLEM (ALSO NAME THE CAD FILE, TO LINK WITH PART)
  // PART IMAGE UPLOAD (UNIQUE FOR EACH PART)
  // Move upload document section
  // MAKE THIS A PAGE
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const apiClient = createApiClient(navigate, dispatch);
  const userName = useSelector((state) => state?.authReducer?.userData?.name);
  const token = useSelector((state) => state.authReducer.token);
  const reduxDump = useSelector((state) => state?.dumpReducer?.partForm);

  var { projectId, projectName } = useParams();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const partData = selectedPart?.part;

  const form = useForm({
    resolver: zodResolver(formSchema),
    mode: 'onBlur',
    reValidateMode: 'onBlur',
    defaultValues: {
      part_name: partData?.part_name || '',
      part_number: partData?.part_number || '',
      part_revision: partData?.part_revision || undefined,
      life_cycle_state: partData?.life_cycle_state || 'prototype',
      part_owner: partData?.part_owner || userName,
      title: partData?.title || '',
      description: partData?.description || '',
      usage_per_product: partData?.usage_per_product || undefined,
      total_parts_required: partData?.total_parts_required || 1,
      precision: partData?.precision || undefined,
      precision_uom: partData?.precision_uom || '',
      length: partData?.length || undefined,
      width: partData?.width || undefined,
      height: partData?.height || undefined,
      dimension_uom: partData?.dimension_uom || '',
      tolerance: partData?.tolerance || undefined,
      type_of_uom: partData?.type_of_uom || '',
      tolerance_uom: partData?.tolerance_uom || '',
      estimated_weight: partData?.estimated_weight || undefined,
      weight_uom: partData?.weight_uom || '',
      additional_note: partData?.additional_note || '',
      material_manufacturing_info: partData?.material_manufacturing_info || [],
      test_methods: partData?.test_methods || [],
    },
  });

  // for nested tolerance uom
  const selectedTypeOfUOM = useWatch({
    control: form.control,
    name: 'type_of_uom',
  });

  useEffect(() => {
    form.setValue('tolerance_uom', '');
  }, [selectedTypeOfUOM]);

  const [formState, setFormState] = useState(state);
  const isDisabled = formState === FORM_STATE_ENUM.DISPLAY_PART;
  const isEditing = formState === FORM_STATE_ENUM.EDIT_FORM;

  const totalPartsRequiredHelper = () => {};

  // for material & manufacturing
  const [materials, setMaterials] = useState(
    partData?.material_manufacturing_info || [],
  );
  const [addMaterial, setAddMaterial] = useState(false);
  const [materialFormError, setMaterialFormError] = useState(null);

  const [materialFormData, setMaterialFormData] = useState({
    material_category: '',
    required_material: '',
    material_standard: '',
    material_compliance: '',
    manufacturing_process: '',
    surface_finish_process: '',
    heat_treatment_process: '',
    document_ids: [],
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setMaterialFormData({ ...materialFormData, [name]: value });
  };

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setMaterialFormData({ ...materialFormData, [name]: files[0] });
  };

  const handleMaterialFormSubmit = (e) => {
    e.preventDefault();
    if (
      !materialFormData.required_material ||
      !materialFormData.material_standard ||
      !materialFormData.material_compliance ||
      !materialFormData.manufacturing_process ||
      !materialFormData.surface_finish_process ||
      !materialFormData.heat_treatment_process
    ) {
      setMaterialFormError('Please fill out all fields before saving.');
      return;
    }

    const newMaterial = { ...materialFormData };
    const updatedMaterials = [...materials, newMaterial];

    setMaterials(updatedMaterials);

    form.setValue('material_manufacturing_info', updatedMaterials, {
      shouldValidate: true,
      shouldDirty: true,
    });

    setMaterialFormData({
      material_category: '',
      required_material: '',
      material_standard: '',
      material_compliance: '',
      manufacturing_process: '',
      surface_finish_process: '',
      heat_treatment_process: '',
      document_ids: [],
    });
    setAddMaterial(false);
    setMaterialFormError(null);
  };

  // for testing and certifications
  const [testMethods, setTestMethods] = useState(partData?.test_methods || []);
  console.log(testMethods);
  const [addTestMethods, setAddTestMethods] = useState(false);
  const [testMethodError, setTestMethodError] = useState(null);

  const [testMethodsFormData, setTestMethodsFormData] = useState({
    test_method_name: '',
    certification_required: false,
  });

  const handleTestMethodSubmit = (e) => {
    e.preventDefault();
    const { test_method_name, certification_required } = testMethodsFormData;

    if (!test_method_name.trim()) {
      setTestMethodError('Please enter a test method.');
      return;
    }

    if (certification_required !== true && certification_required !== false) {
      setTestMethodError('Please specify if certification is required.');
      return;
    }

    const newTestMethod = { ...testMethodsFormData };
    const updatedTestMethods = [...testMethods, newTestMethod];

    setTestMethods(updatedTestMethods);
    form.setValue('test_methods', updatedTestMethods, {
      shouldValidate: true,
      shouldDirty: true,
    });

    setTestMethodsFormData({
      test_method_name: '',
      certification_required: false,
    });
    setTestMethodError(null);
    setAddTestMethods(false);
  };

  const handleCertificationChange = (testMethodName) => {
    setTestMethods((prev) =>
      prev.map((item) =>
        item.test_method_name === testMethodName
          ? { ...item, certification_required: !item.certification_required }
          : item,
      ),
    );
  };

  // for document upload
  const getDocumentTitles = () => {
    let suffix = '';

    if (formState === FORM_STATE_ENUM.CREATING_PART) {
      suffix = selectedPart?.index !== undefined ? `${selectedPart.index}` : '';
    } else {
      suffix = partData?.part_id ? `${partData.part_id}` : '';
    }

    return {
      partImage: `PartImage-${suffix}`,
      otherDocument: `PartOtherDocument-${suffix}`,
    };
  };

  const DOCUMENT_TITLES = getDocumentTitles();

  const [cadFileId, setCadFileId] = useState(
    partData?.cad_file_conversion_id || null,
  );
  const [existingDocuments, setExistingDocuments] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [documentStates, setDocumentStates] = useState({
    partImage: partData?.partImage || null,
    otherDocument: partData?.otherDocument || null,
  });
  const [uploadProgress, setUploadProgress] = useState({
    partImage: 0,
    otherDocument: 0,
  });

  useEffect(() => {
    fetchExistingDocumentsArray(
      partData?.document_ids,
      apiClient,
      setExistingDocuments,
      setError,
    );
  }, [token]);

  //CAD FILE UPLOAD
  const handleCADfileUpload = (fileInfo) => {
    setCadFileId(fileInfo.cad_conversion_id);
  };

  // PART FORM SUBMIT
  const handleSubmit = async (values) => {
    setIsLoading(true);
    setError(null);

    if (formState === FORM_STATE_ENUM.CREATING_PART) {
      try {
        const docIDs = existingDocuments
          .filter(
            (document) =>
              document.document_name === 'PartImage-' ||
              document.document_name === 'PartOtherDocument-',
          )
          .map((document) => document.id);

        const formattedValues = {
          ...values,
          material_manufacturing_info: materials,
          test_methods: testMethods,
          document_ids: docIDs,
          cad_file_conversion_id: cadFileId,
          status: 'Active',
        };

        // CREATING PART
        const response = await apiClient.post(
          '/api/part/create',
          JSON.stringify(formattedValues),
        );
        const responseData = response.data;
        console.log('✅ Part creation successful! with data:', responseData);

        const part_id = responseData?.data?.part_id;
        const part_image_doc = existingDocuments.find(
          (document) => document.document_name === 'PartImage-',
        );
        const other_document_doc = existingDocuments.find(
          (document) => document.document_name === 'PartOtherDocument-',
        );

        // updating document ids
        if (part_image_doc) {
          const partImageUpdateResponse = await apiClient.post(
            '/api/documents/update',
            JSON.stringify({
              document_id: part_image_doc?.id,
              document_name: `PartImage-${part_id}`,
            }),
          );
          const partImageUpdateResponseData = partImageUpdateResponse.data;
          console.log(
            '✅ Part image name updated:',
            partImageUpdateResponseData,
          );
        }

        if (other_document_doc) {
          const otherDocumentUpdateResponse = await apiClient.post(
            '/api/documents/update',
            JSON.stringify({
              document_id: other_document_doc?.id,
              document_name: `PartOtherDocument-${part_id}`,
            }),
          );
          const otherDocumentUpdateResponseData =
            otherDocumentUpdateResponse.data;
          console.log(
            '✅ Other Doccument name updated:',
            otherDocumentUpdateResponseData,
          );
        }

        if (projectId) {
          try {
            // getting all partIds of the project
            let partIds = new Set();

            try {
              const project_all_parts = await apiClient.post(
                '/api/project-part/get',
                {
                  project_id: projectId,
                },
              );

              const response_project_all_parts = project_all_parts.data;
              const fetchedPartIds =
                response_project_all_parts?.data?.parts.map(
                  (part) => part.part_id,
                );

              partIds = new Set(fetchedPartIds); // remove duplicates initially
            } catch (error) {
              const error_message = error?.response?.data?.error_message;
              console.log(error_message || error);
            }

            // Add the new part_id safely (Set ensures uniqueness)
            if (responseData?.data?.part_id) {
              partIds.add(responseData.data.part_id);
            }

            // If you need it as an array later:
            const uniquePartIds = Array.from(partIds);

            //linking part with project
            const linkingResponse = await apiClient.post(
              '/api/project-part/create',
              JSON.stringify({
                project_id: projectId,
                part_ids: uniquePartIds,
              }),
            );
            const linkingResponseData = linkingResponse.data;
            console.log(
              '✅ Part and project linked successful! with data:',
              linkingResponseData,
            );

            setError(null);
          } catch (error) {
            const error_message = error?.response?.data?.error_message;
            console.log(error_message || error);

            setError(
              error_message ||
                'A network error occurred while linking. Please check your connection and try again.',
            );
          }
        }

        onSubmit(responseData?.data);
        onClose();
      } catch (error) {
        const error_message = error?.response?.data?.error_message;
        console.log(error_message || error);

        setError(
          error_message ||
            'A network error occurred. Please check your connection and try again.',
        );
      } finally {
        setIsLoading(false);
      }
    } else if (formState === FORM_STATE_ENUM.EDIT_FORM) {
      const docIDs = existingDocuments
        .filter(
          (document) =>
            document.document_name === `PartImage-${partData?.part_id}` ||
            document.document_name === `PartOtherDocument-${partData?.part_id}`,
        )
        .map((document) => document.id);

      const formattedValues = {
        ...values,
        part_id: partData?.part_id,
        material_manufacturing_info: materials,
        test_methods: testMethods,
        document_ids: docIDs,
        cad_file_conversion_id: cadFileId,
        status: 'Active',
      };

      try {
        const response = await apiClient.put(
          '/api/part/update',
          JSON.stringify(formattedValues),
        );
        const responseData = response.data;
        console.log('✅ Part Updation successful! with data:', responseData);

        try {
          let partIds = new Set();

          try {
            const project_all_parts = await apiClient.post(
              '/api/project-part/get',
              {
                project_id: projectId,
              },
            );

            const response_project_all_parts = project_all_parts.data;
            const fetchedPartIds = response_project_all_parts?.data?.parts.map(
              (part) => part.part_id,
            );

            partIds = new Set(fetchedPartIds); // remove duplicates initially
          } catch (error) {
            const error_message = error?.response?.data?.error_message;
            console.log(error_message || error);
          }

          // Add the new part_id safely (Set ensures uniqueness)
          if (responseData?.data?.part_id) {
            partIds.add(responseData.data.part_id);
          }

          // If you need it as an array later:
          const uniquePartIds = Array.from(partIds);

          //linking part with project
          const linkingResponse = await apiClient.post(
            '/api/project-part/create',
            JSON.stringify({
              project_id: projectId,
              part_ids: uniquePartIds,
            }),
          );
          const linkingResponseData = linkingResponse.data;
          console.log(
            '✅ Part and project updated successful! with data:',
            linkingResponseData,
          );
          setError(null);
        } catch (error) {
          const error_message = error?.response?.data?.error_message;
          console.log(error_message || error);
        }

        onSubmit(responseData?.data);
        onClose();
      } catch (error) {
        const error_message = error?.response?.data?.error_message;
        console.log(error_message || error);

        setError(
          error_message ||
            'A network error occurred. Please check your connection and try again.',
        );
      } finally {
        setIsLoading(false);
      }
    }
  };

  return (
    <div className="bg-white rounded-lg font-roboto">
      <div className="py-2 px-4">
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(handleSubmit)}
            onKeyDown={(e) => {
              if (
                e.key === 'Enter' &&
                e.target.tagName === 'INPUT' &&
                e.target.tagName !== 'TEXTAREA' &&
                !e.target.getAttribute('data-allow-enter')
              ) {
                e.preventDefault();
              }
            }}
            className=""
          >
            <div className="flex justify-between items-center mb-6 mt-1 ml-6 mr-8">
              <p className="text-lg text-darkPurple font-medium">
                Part Details
              </p>
              {formState === FORM_STATE_ENUM.DISPLAY_PART && (
                <Button
                  variant="outline"
                  disabled={isLoading}
                  className={`w-[80px] h-[30px] text-base text-white transition ease-in-out duration-300 ${
                    isLoading
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-darkPurple hover:bg-darkerPurple hover:text-white'
                  } flex items-center justify-center`}
                  onClick={(e) => {
                    e.preventDefault();
                    setFormState(FORM_STATE_ENUM.EDIT_FORM);
                  }}
                >
                  <Edit2 className="h-4 w-4" />
                  Edit
                </Button>
              )}
            </div>

            {/* Basic Information Section */}
            <div className="bg-gray-50 mx-6 p-4 rounded">
              <h2 className="text-base text-darkPurple bg-gray-100 rounded px-1 font-medium mb-7">
                Basic Information
              </h2>
              <div className="grid grid-cols-4 gap-6">
                <div className="flex flex-col col-span-3 space-y-5">
                  <FormField
                    control={form.control}
                    name="part_number"
                    render={({ field }) => (
                      <FormItem className="group ease-in duration-300 space-y-1">
                        <FormLabel className="text-gray-600 group-hover:text-lightPurple">
                          Part Number
                        </FormLabel>
                        <FormControl>
                          {formState === FORM_STATE_ENUM.DISPLAY_PART ||
                          formState === FORM_STATE_ENUM.DISPLAY_PART_RFQ ? (
                            <p className="w-2/3 text-sm text-black bg-gray-100 px-3 py-2 rounded">
                              {field.value || '—'}
                            </p>
                          ) : (
                            <Input
                              {...field}
                              placeholder="Enter the part number (e.g., A123)"
                              className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-gray-400 border border-gray-300 group-hover:border-lightPurple"
                            />
                          )}
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="part_name"
                    render={({ field, fieldState }) => (
                      <FormItem className="group ease-in duration-300 space-y-1">
                        <FormLabel className="text-gray-600 group-hover:text-lightPurple">
                          Part Name
                        </FormLabel>
                        <FormControl>
                          {formState === FORM_STATE_ENUM.DISPLAY_PART ||
                          formState === FORM_STATE_ENUM.DISPLAY_PART_RFQ ? (
                            <p className="w-2/3 text-sm text-black bg-gray-100 px-3 py-2 rounded">
                              {field.value || '—'}
                            </p>
                          ) : (
                            <Input
                              {...field}
                              placeholder="Enter the part name"
                              className={`focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-gray-400 border border-gray-300 group-hover:border-lightPurple [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                            />
                          )}
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="part_revision"
                    render={({ field, fieldState }) => (
                      <FormItem className="group ease-in duration-300 space-y-1">
                        <FormLabel className="text-gray-600 group-hover:text-lightPurple">
                          Part Revision
                        </FormLabel>
                        <FormControl>
                          {formState === FORM_STATE_ENUM.DISPLAY_PART ||
                          formState === FORM_STATE_ENUM.DISPLAY_PART_RFQ ? (
                            <p className="w-2/3 text-sm text-black bg-gray-100 px-3 py-2 rounded">
                              {field.value || '—'}
                            </p>
                          ) : (
                            <Input
                              {...field}
                              type="number"
                              step="1"
                              className={`focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-gray-400 border border-gray-300 group-hover:border-lightPurple [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                              onChange={(e) => {
                                field.onChange(
                                  e.target.value === ''
                                    ? ''
                                    : Number(e.target.value),
                                );
                              }}
                              onKeyDown={(e) => {
                                if (['e', 'E', '+', '-'].includes(e.key)) {
                                  e.preventDefault();
                                }
                              }}
                              onWheel={(e) => e.target.blur()}
                              placeholder="Enter the part revision"
                            />
                          )}
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
                <div>
                  <FormField
                    control={form.control}
                    name="partImage"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-foreground text-gray-600">
                          Part Image
                        </FormLabel>
                        <FormControl>
                          {formState === FORM_STATE_ENUM.DISPLAY_PART ||
                          formState === FORM_STATE_ENUM.DISPLAY_PART_RFQ ? (
                            existingDocuments.some(
                              (doc) =>
                                doc.document_name === DOCUMENT_TITLES.partImage,
                            ) ? (
                              <FileUpload
                                DOCUMENT_TITLES={DOCUMENT_TITLES}
                                MAX_FILE_SIZE={MAX_FILE_SIZE}
                                ACCEPTED_FILE_TYPES={ACCEPTED_FILE_TYPES_IMAGE}
                                existingDocuments={existingDocuments}
                                setExistingDocuments={setExistingDocuments}
                                documentStates={documentStates}
                                setDocumentStates={setDocumentStates}
                                uploadProgress={uploadProgress}
                                setUploadProgress={setUploadProgress}
                                isUploading={isUploading}
                                setIsUploading={setIsUploading}
                                setError={setError}
                                editState={
                                  formState === FORM_STATE_ENUM.EDIT_FORM
                                }
                                key="partImage"
                                id="partImage"
                                label="Upload Part Image"
                                documentType={DOCUMENT_TITLES.partImage}
                                classNames="w-full h-[210px]"
                                existingDocument={existingDocuments.find(
                                  (doc) =>
                                    doc.document_name ===
                                    DOCUMENT_TITLES.partImage,
                                )}
                              />
                            ) : (
                              <p className="w-full h-[210px] text-sm text-black bg-gray-100 px-3 py-2 rounded">
                                {field.value || 'No Part Image'}
                              </p>
                            )
                          ) : (
                            <FileUpload
                              DOCUMENT_TITLES={DOCUMENT_TITLES}
                              MAX_FILE_SIZE={MAX_FILE_SIZE}
                              ACCEPTED_FILE_TYPES={ACCEPTED_FILE_TYPES_IMAGE}
                              existingDocuments={existingDocuments}
                              setExistingDocuments={setExistingDocuments}
                              documentStates={documentStates}
                              setDocumentStates={setDocumentStates}
                              uploadProgress={uploadProgress}
                              setUploadProgress={setUploadProgress}
                              isUploading={isUploading}
                              setIsUploading={setIsUploading}
                              setError={setError}
                              key="partImage"
                              id="partImage"
                              label="Upload Part Image"
                              documentType={DOCUMENT_TITLES.partImage}
                              classNames="w-full h-[210px]"
                              existingDocument={existingDocuments.find(
                                (doc) =>
                                  doc.document_name ===
                                  DOCUMENT_TITLES.partImage,
                              )}
                            />
                          )}
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 mt-5">
                <FormField
                  control={form.control}
                  name="life_cycle_state"
                  render={({ field }) => (
                    <FormItem className="group ease-in duration-300 space-y-1">
                      <FormLabel className="text-gray-600 group-hover:text-lightPurple">
                        Lifecycle State
                      </FormLabel>
                      <FormControl>
                        {formState === FORM_STATE_ENUM.DISPLAY_PART ||
                        formState === FORM_STATE_ENUM.DISPLAY_PART_RFQ ? (
                          <p className="w-full text-sm text-black bg-gray-100 px-3 py-2 rounded">
                            {field.value || '—'}
                          </p>
                        ) : (
                          <Input
                            {...field}
                            className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-gray-400 border border-gray-300 group-hover:border-lightPurple disabled:border disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-500 disabled:text-black disabled:bg-white"
                            disabled
                          />
                        )}
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="flex items-end gap-2">
                  <FormField
                    control={form.control}
                    name="part_owner"
                    render={({ field }) => (
                      <FormItem className="flex-1 group ease-in duration-300 space-y-1">
                        <FormLabel className="text-gray-600 group-hover:text-lightPurple">
                          Part Owner
                        </FormLabel>
                        <FormControl>
                          {formState === FORM_STATE_ENUM.DISPLAY_PART ||
                          formState === FORM_STATE_ENUM.DISPLAY_PART_RFQ ? (
                            <p className="w-full text-sm text-black bg-gray-100 px-3 py-2 rounded">
                              {field.value || '—'}
                            </p>
                          ) : (
                            <Input
                              {...field}
                              disabled
                              className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-gray-400 border border-gray-300 disabled:border disabled:border-gray-300 disabled:bg-gray-100 disabled:text-gray-900 group-hover:border-lightPurple disabled:bg-white"
                            />
                          )}
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem className="mt-5 group ease-in duration-300 space-y-1">
                    <FormLabel className="text-gray-600 group-hover:text-lightPurple">
                      Description
                    </FormLabel>
                    <FormControl>
                      {formState === FORM_STATE_ENUM.DISPLAY_PART ||
                      formState === FORM_STATE_ENUM.DISPLAY_PART_RFQ ? (
                        <p className="w-full text-sm text-black bg-gray-100 px-3 py-2 rounded">
                          {field.value || '—'}
                        </p>
                      ) : (
                        <Textarea
                          {...field}
                          placeholder="Enter the part Description"
                          className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-gray-400 border border-gray-300 group-hover:border-lightPurple"
                        />
                      )}
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Measurement & Quantity */}
            <div className="bg-gray-50 mx-6 p-4 rounded mt-12">
              <h2 className="text-base text-darkPurple bg-gray-100 rounded px-1 font-medium mb-7">
                Measurement & Quantity
              </h2>

              <div className="grid grid-cols-7 gap-2">
                <FormField
                  control={form.control}
                  name="usage_per_product"
                  render={({ field }) => (
                    <FormItem className="col-span-2 group ease-in duration-300 space-y-1">
                      <FormLabel className="text-gray-600 group-hover:text-lightPurple">
                        Usage Per Product
                      </FormLabel>
                      <FormControl>
                        {formState === FORM_STATE_ENUM.DISPLAY_PART ||
                        formState === FORM_STATE_ENUM.DISPLAY_PART_RFQ ? (
                          <p className="w-full text-sm text-black bg-gray-100 px-3 py-2 rounded">
                            {field.value || '—'}
                          </p>
                        ) : (
                          <Input
                            {...field}
                            type="number"
                            step="1"
                            className={`focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-gray-400 border border-gray-300 group-hover:border-lightPurple [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                            onChange={(e) => {
                              field.onChange(
                                e.target.value === ''
                                  ? ''
                                  : Number(e.target.value),
                              );
                            }}
                            onKeyDown={(e) => {
                              if (['e', 'E', '+', '-'].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            onWheel={(e) => e.target.blur()}
                            placeholder="Enter Number Usage per Product"
                          />
                        )}
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="total_parts_required"
                  render={({ field }) => (
                    <FormItem className="col-span-2 group ease-in duration-300 space-y-1">
                      <FormLabel className="text-gray-600 group-hover:text-lightPurple">
                        Total Parts Required
                      </FormLabel>
                      <FormControl>
                        {formState === FORM_STATE_ENUM.DISPLAY_PART ||
                        formState === FORM_STATE_ENUM.DISPLAY_PART_RFQ ? (
                          <p className="w-full text-sm text-black bg-gray-100 px-3 py-2 rounded">
                            {field.value || '—'}
                          </p>
                        ) : (
                          <Input
                            {...field}
                            placeholder="Auto Calculated"
                            className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-gray-400 border border-gray-300 group-hover:border-lightPurple disabled:text-black disabled:bg-white"
                            disabled
                          />
                        )}
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="precision"
                  render={({ field }) => (
                    <FormItem className="col-span-2 group ease-in duration-300 space-y-1">
                      <FormLabel className="text-gray-600 group-hover:text-lightPurple">
                        Precision
                      </FormLabel>
                      <FormControl>
                        {formState === FORM_STATE_ENUM.DISPLAY_PART ||
                        formState === FORM_STATE_ENUM.DISPLAY_PART_RFQ ? (
                          <p className="w-full text-sm text-black bg-gray-100 px-3 py-2 rounded">
                            {field.value || '—'}
                          </p>
                        ) : (
                          <Input
                            {...field}
                            type="number"
                            className={`focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-gray-400 border border-gray-300 group-hover:border-lightPurple [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                            onChange={(e) => {
                              field.onChange(
                                e.target.value === ''
                                  ? ''
                                  : Number(e.target.value),
                              );
                            }}
                            onKeyDown={(e) => {
                              if (['e', 'E', '+', '-'].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            onWheel={(e) => e.target.blur()}
                            placeholder="Enter the precision (e.g., ±0.01mm)"
                          />
                        )}
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="precision_uom"
                  render={({ field }) => (
                    <FormItem className="mt-7 group ease-in duration-300 ">
                      {formState === FORM_STATE_ENUM.DISPLAY_PART ||
                      formState === FORM_STATE_ENUM.DISPLAY_PART_RFQ ? (
                        <p className="w-full text-sm text-black bg-gray-100 px-3 py-2 rounded">
                          {field.value || '—'}
                        </p>
                      ) : (
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple group-hover:border-lightPurple">
                              {field.value || (
                                <span className="text-slate-500">UOM</span>
                              )}
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem
                              value="pcs"
                              className="focus:bg-veryLightPurple focus:bg-opacity-15"
                            >
                              Pieces
                            </SelectItem>
                            <SelectItem
                              value="sets"
                              className="focus:bg-veryLightPurple focus:bg-opacity-15"
                            >
                              Sets
                            </SelectItem>
                            <SelectItem
                              value="units"
                              className="focus:bg-veryLightPurple focus:bg-opacity-15"
                            >
                              Units
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      )}

                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="mt-5">
                <p className=" text-sm text-gray-600 font-medium">
                  Product Dimensions
                </p>
                <div className="grid grid-cols-7 gap-2">
                  {['length', 'width', 'height'].map((name) => (
                    <FormField
                      key={name}
                      control={form.control}
                      name={name}
                      render={({ field }) => (
                        <FormItem className="col-span-2 group ease-in duration-300">
                          <FormControl>
                            {formState === FORM_STATE_ENUM.DISPLAY_PART ||
                            formState === FORM_STATE_ENUM.DISPLAY_PART_RFQ ? (
                              <p className="w-full text-sm text-black bg-gray-100 px-3 py-2 rounded">
                                {field.value || '—'}
                              </p>
                            ) : (
                              <Input
                                {...field}
                                type="number"
                                className={`focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-gray-400 border border-gray-300 group-hover:border-lightPurple [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                                onChange={(e) => {
                                  field.onChange(
                                    e.target.value === ''
                                      ? ''
                                      : Number(e.target.value),
                                  );
                                }}
                                onKeyDown={(e) => {
                                  if (['e', 'E', '+', '-'].includes(e.key)) {
                                    e.preventDefault();
                                  }
                                }}
                                onWheel={(e) => e.target.blur()}
                                placeholder={`Item ${name.replace('item', '').toLowerCase()}`}
                              />
                            )}
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />
                  ))}

                  <FormField
                    control={form.control}
                    name="dimension_uom"
                    render={({ field }) => (
                      <FormItem className="group ease-in duration-300">
                        {formState === FORM_STATE_ENUM.DISPLAY_PART ||
                        formState === FORM_STATE_ENUM.DISPLAY_PART_RFQ ? (
                          <p className="w-full text-sm text-black bg-gray-100 px-3 py-2 rounded">
                            {field.value || '—'}
                          </p>
                        ) : (
                          <Select
                            onValueChange={field.onChange}
                            defaultValue={field.value}
                          >
                            <FormControl>
                              <SelectTrigger className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple group-hover:border-lightPurple">
                                {field.value || (
                                  <span className="text-slate-500">UOM</span>
                                )}
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              <SelectItem
                                value="smm"
                                className="focus:bg-veryLightPurple focus:bg-opacity-15"
                              >
                                MM
                              </SelectItem>
                              <SelectItem
                                value="cm"
                                className="focus:bg-veryLightPurple focus:bg-opacity-15"
                              >
                                CM
                              </SelectItem>
                              <SelectItem
                                value="m"
                                className="focus:bg-veryLightPurple focus:bg-opacity-15"
                              >
                                M
                              </SelectItem>
                              <SelectItem
                                value="inch"
                                className="focus:bg-veryLightPurple focus:bg-opacity-15"
                              >
                                IN
                              </SelectItem>
                              <SelectItem
                                value="feet"
                                className="focus:bg-veryLightPurple focus:bg-opacity-15"
                              >
                                FT
                              </SelectItem>
                              <SelectItem
                                value="yard"
                                className="focus:bg-veryLightPurple focus:bg-opacity-15"
                              >
                                YD
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        )}

                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>
              </div>

              <div className="grid grid-cols-8 gap-2 mt-5">
                <FormField
                  control={form.control}
                  name="tolerance"
                  render={({ field }) => (
                    <FormItem className="col-span-4 group ease-in duration-300 space-y-1">
                      <FormLabel className="text-gray-600 group-hover:text-lightPurple">
                        Tolerance
                      </FormLabel>
                      <FormControl>
                        {formState === FORM_STATE_ENUM.DISPLAY_PART ||
                        formState === FORM_STATE_ENUM.DISPLAY_PART_RFQ ? (
                          <p className="w-full text-sm text-black bg-gray-100 px-3 py-2 rounded">
                            {field.value || '—'}
                          </p>
                        ) : (
                          <Input
                            {...field}
                            type="number"
                            placeholder="Enter the Tolerance"
                            className={`focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-gray-400 border border-gray-300 group-hover:border-lightPurple [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                            onChange={(e) => {
                              field.onChange(
                                e.target.value === ''
                                  ? ''
                                  : Number(e.target.value),
                              );
                            }}
                            onKeyDown={(e) => {
                              if (['e', 'E', '+', '-'].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            onWheel={(e) => e.target.blur()}
                          />
                        )}
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="type_of_uom"
                  render={({ field }) => (
                    <FormItem className="mt-7 group ease-in duration-300">
                      {formState === FORM_STATE_ENUM.DISPLAY_PART ||
                      formState === FORM_STATE_ENUM.DISPLAY_PART_RFQ ? (
                        <p className="w-full text-sm text-black bg-gray-100 px-3 py-2 rounded">
                          {field.value || '—'}
                        </p>
                      ) : (
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple group-hover:border-lightPurple">
                              {field.value || (
                                <span className="text-slate-500">
                                  Type of UOM
                                </span>
                              )}
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem
                              value="length"
                              className="focus:bg-veryLightPurple focus:bg-opacity-15"
                            >
                              Length
                            </SelectItem>
                            <SelectItem
                              value="volume"
                              className="focus:bg-veryLightPurple focus:bg-opacity-15"
                            >
                              Volume
                            </SelectItem>
                            <SelectItem
                              value="liquid_volume"
                              className="focus:bg-veryLightPurple focus:bg-opacity-15"
                            >
                              Liquid Volume
                            </SelectItem>
                            <SelectItem
                              value="weight"
                              className="focus:bg-veryLightPurple focus:bg-opacity-15"
                            >
                              Weight
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="tolerance_uom"
                  render={({ field }) => (
                    <FormItem className="mt-7 group ease-in duration-300">
                      {formState === FORM_STATE_ENUM.DISPLAY_PART ||
                      formState === FORM_STATE_ENUM.DISPLAY_PART_RFQ ? (
                        <p className="w-full text-sm text-black bg-gray-100 px-3 py-2 rounded">
                          {field.value || '—'}
                        </p>
                      ) : (
                        <Select
                          onValueChange={field.onChange}
                          value={field.value}
                          disabled={!selectedTypeOfUOM}
                        >
                          <FormControl>
                            <SelectTrigger className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple group-hover:border-lightPurple">
                              {field.value || (
                                <span className="text-slate-500">UOM</span>
                              )}
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {(UOM_OPTIONS[selectedTypeOfUOM] || []).map(
                              (uom) => (
                                <SelectItem
                                  key={uom}
                                  value={uom}
                                  className="focus:bg-veryLightPurple focus:bg-opacity-15"
                                >
                                  {uom}
                                </SelectItem>
                              ),
                            )}
                          </SelectContent>
                        </Select>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-8 gap-2 mt-5">
                <FormField
                  control={form.control}
                  name="estimated_weight"
                  render={({ field }) => (
                    <FormItem className="col-span-4 group ease-in duration-300 space-y-1">
                      <FormLabel className="text-gray-600 group-hover:text-lightPurple">
                        Estimated Weight
                      </FormLabel>
                      <FormControl>
                        {formState === FORM_STATE_ENUM.DISPLAY_PART ||
                        formState === FORM_STATE_ENUM.DISPLAY_PART_RFQ ? (
                          <p className="w-full text-sm text-black bg-gray-100 px-3 py-2 rounded">
                            {field.value || '—'}
                          </p>
                        ) : (
                          <Input
                            {...field}
                            type="number"
                            placeholder="Enter Weight"
                            className={`focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-gray-400 border border-gray-300 group-hover:border-lightPurple [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none`}
                            onChange={(e) => {
                              field.onChange(
                                e.target.value === ''
                                  ? ''
                                  : Number(e.target.value),
                              );
                            }}
                            onKeyDown={(e) => {
                              if (['e', 'E', '+', '-'].includes(e.key)) {
                                e.preventDefault();
                              }
                            }}
                            onWheel={(e) => e.target.blur()}
                          />
                        )}
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="weight_uom"
                  render={({ field }) => (
                    <FormItem className="mt-7 group ease-in duration-300">
                      {formState === FORM_STATE_ENUM.DISPLAY_PART ||
                      formState === FORM_STATE_ENUM.DISPLAY_PART_RFQ ? (
                        <p className="w-full text-sm text-black bg-gray-100 px-3 py-2 rounded">
                          {field.value || '—'}
                        </p>
                      ) : (
                        <Select
                          onValueChange={field.onChange}
                          defaultValue={field.value}
                        >
                          <FormControl>
                            <SelectTrigger className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple group-hover:border-lightPurple">
                              {field.value || (
                                <span className="text-slate-500">UOM</span>
                              )}
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            <SelectItem
                              value="milli_gram"
                              className="focus:bg-veryLightPurple focus:bg-opacity-15"
                            >
                              MG
                            </SelectItem>
                            <SelectItem
                              value="gram"
                              className="focus:bg-veryLightPurple focus:bg-opacity-15"
                            >
                              GM
                            </SelectItem>
                            <SelectItem
                              value="kilo_gram"
                              className="focus:bg-veryLightPurple focus:bg-opacity-15"
                            >
                              KG
                            </SelectItem>
                            <SelectItem
                              value="pounds"
                              className="focus:bg-veryLightPurple focus:bg-opacity-15"
                            >
                              LB
                            </SelectItem>
                            <SelectItem
                              value="ounce"
                              className="focus:bg-veryLightPurple focus:bg-opacity-15"
                            >
                              OZ
                            </SelectItem>
                            <SelectItem
                              value="tone"
                              className="focus:bg-veryLightPurple focus:bg-opacity-15"
                            >
                              T
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Material & Manufacturing */}
            <div className="bg-gray-50 mx-6 p-4 rounded mt-12">
              <h2 className="text-base text-darkPurple bg-gray-100 rounded px-1 font-medium mb-7">
                Material & Manufacturing
              </h2>

              {materials.length > 0 ? (
                <>
                  <div className="grid grid-cols-8 gap-4 text-black bg-veryVeryLightPurple text-base border py-4 rounded-md px-2">
                    <div className="col-span-1 text-center">
                      Material Category
                    </div>
                    <div className="col-span-1 text-center">
                      Required Materials
                    </div>
                    <div className="col-span-1 text-center">
                      Material Standard
                    </div>
                    <div className="col-span-1 text-center">
                      Material Complience
                    </div>
                    <div className="col-span-1 text-center">
                      Manufacturing Processes Needed
                    </div>
                    <div className="col-span-1 text-center">
                      Surface Finish Process
                    </div>
                    <div className="col-span-1 text-center">Heat Treatment</div>
                    <div className="col-span-1 text-center">Uploads</div>
                  </div>

                  {materials.map((material, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-8 gap-4 mt-2 items-center py-4 text-base bg-white rounded-md px-2"
                    >
                      <div className="col-span-1 text-center">
                        {material?.material_category}
                      </div>
                      <div className="col-span-1 text-center">
                        {material.required_material}
                      </div>
                      <div className="col-span-1 text-center">
                        {material.material_standard}
                      </div>
                      <div className="col-span-1 text-center">
                        {material.material_compliance}
                      </div>
                      <div className="col-span-1 text-center">
                        ${material.manufacturing_process}
                      </div>
                      <div className="col-span-1 text-center">
                        {material.surface_finish_process}
                      </div>
                      <div className="col-span-1 text-center">
                        {material.heat_treatment_process}
                      </div>
                    </div>
                  ))}
                </>
              ) : (
                <div className="flex items-center justify-center h-32 text-gray-600 font-medium text-lg">
                  You have no materials added in this part,
                  {formState === FORM_STATE_ENUM.DISPLAY_PART &&
                  formState === FORM_STATE_ENUM.DISPLAY_PART_RFQ
                    ? ' Edit to add Material'
                    : ' Proceed to add a material'}
                </div>
              )}

              {formState !== FORM_STATE_ENUM.DISPLAY_PART &&
                formState !== FORM_STATE_ENUM.DISPLAY_PART_RFQ &&
                !addMaterial && (
                  <Button
                    variant="outline"
                    className="flex items-center text-[16px] text-darkPurple border-darkPurple hover:text-darkerPurple hover:bg-veryLightPurple hover:bg-opacity-15 ease-in duration-200 mt-4"
                    onClick={() => {
                      setAddMaterial(true);
                    }}
                  >
                    <Plus className="h-7 w-7" />
                    Add
                  </Button>
                )}

              {formState !== FORM_STATE_ENUM.DISPLAY_PART &&
                formState !== FORM_STATE_ENUM.DISPLAY_PART_RFQ &&
                addMaterial && (
                  <div className="mt-8 p-4 rounded-lg bg-white">
                    <div className="mb-6 w-2/5">
                      <label className="text-gray-600 block text-sm font-medium mb-1">
                        Material Category
                      </label>
                      <Input
                        type="text"
                        name="material_category"
                        value={materialFormData.material_category}
                        onChange={handleChange}
                        placeholder="Select Materials"
                        className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-gray-400 border border-gray-300 group-hover:border-lightPurple"
                      />
                    </div>
                    <div className="grid grid-cols-3 gap-6">
                      <div>
                        <label className="text-gray-600 block text-sm font-medium mb-1">
                          Required Materials
                        </label>
                        <Input
                          type="text"
                          name="required_material"
                          value={materialFormData.required_material}
                          onChange={handleChange}
                          placeholder="Select Materials"
                          className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-gray-400 border border-gray-300 group-hover:border-lightPurple"
                        />
                      </div>
                      <div>
                        <label className="text-gray-600 block text-sm font-medium mb-1">
                          Material Standard
                        </label>
                        <Input
                          type="text"
                          name="material_standard"
                          value={materialFormData.material_standard}
                          onChange={handleChange}
                          placeholder="Enter Standard (e.g., ASTM 240. 304L)"
                          className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-gray-400 border border-gray-300 group-hover:border-lightPurple"
                        />
                      </div>
                      <div>
                        <label className="text-gray-600 block text-sm font-medium mb-1">
                          Material Compliance
                        </label>
                        <Input
                          type="text"
                          name="material_compliance"
                          value={materialFormData.material_compliance}
                          onChange={handleChange}
                          placeholder="Enter Compliance Standard (e.g., ELV)"
                          className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-gray-400 border border-gray-300 group-hover:border-lightPurple"
                        />
                      </div>
                      <div>
                        <label className="text-gray-600 block text-sm font-medium mb-1">
                          Manufacturing Processes Needed
                        </label>
                        <Input
                          type="text"
                          name="manufacturing_process"
                          value={materialFormData.manufacturing_process}
                          onChange={handleChange}
                          placeholder="Select Process (e.g., Injection Molding)"
                          className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-gray-400 border border-gray-300 group-hover:border-lightPurple"
                        />
                      </div>
                      <div>
                        <label className="text-gray-600 block text-sm font-medium mb-1">
                          Surface Finish Process
                        </label>
                        <Input
                          type="text"
                          name="surface_finish_process"
                          value={materialFormData.surface_finish_process}
                          onChange={handleChange}
                          placeholder="Enter Process (e.g., Chrome Plating)"
                          className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-gray-400 border border-gray-300 group-hover:border-lightPurple"
                        />
                      </div>
                      <div>
                        <label className="text-gray-600 block text-sm font-medium mb-1">
                          Heat Treatment
                        </label>
                        <Input
                          type="text"
                          name="heat_treatment_process"
                          value={materialFormData.heat_treatment_process}
                          onChange={handleChange}
                          placeholder="Enter Treatment (e.g., Tempering)"
                          className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-gray-400 border border-gray-300 group-hover:border-lightPurple"
                        />
                      </div>

                      <div>
                        <label className="text-gray-600 block text-sm font-medium mb-1">
                          Manufacturing Process Specification
                        </label>
                        <Input
                          type="file"
                          name="processSpec"
                          onChange={handleFileChange}
                          className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-gray-400 border border-gray-300 group-hover:border-lightPurple"
                        />
                      </div>
                      <div>
                        <label className="text-gray-600 block text-sm font-medium mb-1">
                          Surface Finish Specification
                        </label>
                        <Input
                          type="file"
                          name="finishSpec"
                          onChange={handleFileChange}
                          className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-gray-400 border border-gray-300 group-hover:border-lightPurple"
                        />
                      </div>
                      <div>
                        <label className="text-gray-600 block text-sm font-medium">
                          Heat Treatment Specification
                        </label>
                        <Input
                          type="file"
                          name="heatSpec"
                          onChange={handleFileChange}
                          className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-gray-400 border border-gray-300 group-hover:border-lightPurple"
                        />
                      </div>
                    </div>

                    {materialFormError && (
                      <div className="w-full flex items-center justify-center mt-4 ">
                        <p className="bg-[#F8D7D7] text-[#B64444] text-sm py-3 px-3 rounded-lg">
                          {materialFormError}
                        </p>
                      </div>
                    )}

                    <div className="mt-4 flex gap-4 justify-center">
                      <button
                        onClick={() => {
                          setAddMaterial(false);
                          setMaterialFormError(null);
                        }}
                        className="mt-4 bg-white text-darkPurple border border-darkPurple px-4 py-2 rounded hover:bg-gray-100 ease-in duration-300"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleMaterialFormSubmit}
                        className="mt-4 bg-darkPurple text-white px-4 py-2 rounded hover:bg-darkerPurple ease-in duration-300"
                      >
                        Save
                      </button>
                    </div>
                  </div>
                )}
            </div>
            {form.formState.errors.material_manufacturing_info?.message && (
              <p className="text-sm text-red-600 font-medium mt-3 mx-6">
                {form.formState.errors.material_manufacturing_info.message}
              </p>
            )}

            {/* Testing & Certifications */}
            <div className="bg-gray-50 mx-6 p-4 rounded mt-12">
              <h2 className="text-base text-darkPurple bg-gray-100 rounded px-1 font-medium mb-7">
                Testing & Certifications
              </h2>

              <div className="w-full border rounded p-2">
                {/* Header Row */}
                <div className="grid grid-cols-3 p-2 bg-veryVeryLightPurple font-medium">
                  <div className="pl-2 col-span-2">Test Method</div>
                  <div className="w-full text-center">
                    Certification Required
                  </div>
                </div>

                {/* Data Rows */}
                <div className="grid grid-cols-3 gap-2 p-2">
                  {testMethods && testMethods.length > 0 ? (
                    testMethods.map((item, index) => (
                      <>
                        <div className="mt-5 col-span-2 px-2">
                          {item.test_method_name}
                        </div>
                        <div className="mt-5 flex justify-center">
                          <input
                            type="checkbox"
                            className="w-6 h-6 accent-darkPurple"
                            disabled={
                              formState === FORM_STATE_ENUM.DISPLAY_PART ||
                              formState === FORM_STATE_ENUM.DISPLAY_PART_RFQ
                            }
                            checked={item.certification_required || false}
                            onChange={() =>
                              handleCertificationChange(item.test_method_name)
                            }
                          />
                        </div>
                      </>
                    ))
                  ) : (
                    <div className="col-span-3 flex items-center justify-center h-32 text-gray-600 font-medium text-lg">
                      You have no testing added in this part,{' '}
                      {formState === FORM_STATE_ENUM.DISPLAY_PART &&
                      formState === FORM_STATE_ENUM.DISPLAY_PART_RFQ
                        ? 'Edit to add Tests'
                        : 'Proceed to add Tests'}
                    </div>
                  )}

                  {/* Input Row */}
                  {formState !== FORM_STATE_ENUM.DISPLAY_PART &&
                    formState !== FORM_STATE_ENUM.DISPLAY_PART_RFQ && (
                      <>
                        <div className="col-span-2 mt-5">
                          <Input
                            type="text"
                            name="method"
                            value={testMethodsFormData.test_method_name}
                            onChange={(e) =>
                              setTestMethodsFormData({
                                ...testMethodsFormData,
                                test_method_name: e.target.value,
                              })
                            }
                            className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-gray-400 border border-gray-300 group-hover:border-lightPurple"
                            placeholder="Enter test method required"
                          />
                        </div>
                        <div className="mt-7 flex justify-center">
                          <Input
                            type="checkbox"
                            name="certification_required"
                            checked={
                              testMethodsFormData.certification_required ===
                              true
                            }
                            onChange={(e) =>
                              setTestMethodsFormData({
                                ...testMethodsFormData,
                                certification_required: e.target.checked
                                  ? true
                                  : false,
                              })
                            }
                            className="w-6 h-6 accent-darkPurple"
                          />
                        </div>
                      </>
                    )}

                  {/* Add Button */}
                  {formState !== FORM_STATE_ENUM.DISPLAY_PART &&
                    formState !== FORM_STATE_ENUM.DISPLAY_PART_RFQ && (
                      <div className="mt-4 col-span-3">
                        <Button
                          variant="outline"
                          className="flex items-center text-[16px] text-darkPurple border-darkPurple hover:text-darkerPurple hover:bg-veryLightPurple hover:bg-opacity-15 ease-in duration-200"
                          onClick={handleTestMethodSubmit}
                        >
                          <Plus className="h-7 w-7" />
                          Add
                        </Button>
                      </div>
                    )}

                  {testMethodError && (
                    <div className="col-span-3 w-full flex items-center justify-center mt-4 ">
                      <p className="bg-[#F8D7D7] text-[#B64444] text-sm py-3 px-3 rounded-lg">
                        {testMethodError}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            {form.formState.errors.test_methods?.message && (
              <p className="text-sm text-red-600 font-medium mt-3 mx-6">
                {form.formState.errors.test_methods?.message}
              </p>
            )}

            {/* Upload CAD File & Other Documents */}
            <div className="bg-gray-50 mx-6 p-4 rounded mt-12">
              <h2 className="text-base text-darkPurple bg-gray-100 rounded px-1 font-medium mb-7">
                Upload CAD File & Other Documents
              </h2>

              <p className="w-5/6 text-[13px] text-gray-500 mt-1">
                Upload a CAD file to help manufacturers understand your design,
                ensure accurate quotes, and collaborate seamlessly using our
                interactive 3D viewer by adding comments, highlighting areas of
                concern, and discussing modifications
              </p>

              <FormField
                control={form.control}
                name="partCADFile"
                render={({ field }) => (
                  <FormItem className="mt-4">
                    <FormControl>
                      {formState === FORM_STATE_ENUM.DISPLAY_PART ||
                      formState === FORM_STATE_ENUM.DISPLAY_PART_RFQ ? (
                        cadFileId ? (
                          <CADFileUploader
                            screenState={formState}
                            initialKey={cadFileId}
                            onFileUpload={handleCADfileUpload}
                            classNames="w-52 h-12"
                            label="Uploaded CAD file"
                          />
                        ) : (
                          <p className="w-52 h-12 text-sm text-black bg-gray-100 px-3 py-2 rounded">
                            No CAD File
                          </p>
                        )
                      ) : (
                        <CADFileUploader
                          onFileUpload={handleCADfileUpload}
                          classNames="w-52 h-12"
                        />
                      )}
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <p className="text-xs text-gray-500 mt-5">
                Upload any relevant files to provide additional context for
                manufacturers.
              </p>
              <p className="text-xs text-gray-500 mt-1">
                Supported file types: PDF, CAD, DOC, Max size: 20MB per file
                123123123123123
              </p>

              <FormField
                control={form.control}
                name="otherDocument"
                render={({ field }) => (
                  <FormItem className="mt-4">
                    <FormControl>
                      {formState === FORM_STATE_ENUM.DISPLAY_PART ||
                      formState === FORM_STATE_ENUM.DISPLAY_PART_RFQ ? (
                        existingDocuments.some(
                          (doc) =>
                            doc.document_name === DOCUMENT_TITLES.otherDocument,
                        ) ? (
                          <div className="flex items-center justify-start">
                            <FileUpload
                              DOCUMENT_TITLES={DOCUMENT_TITLES}
                              MAX_FILE_SIZE={MAX_FILE_SIZE}
                              ACCEPTED_FILE_TYPES={ACCEPTED_FILE_TYPES}
                              existingDocuments={existingDocuments}
                              setExistingDocuments={setExistingDocuments}
                              documentStates={documentStates}
                              setDocumentStates={setDocumentStates}
                              uploadProgress={uploadProgress}
                              setUploadProgress={setUploadProgress}
                              isUploading={isUploading}
                              setIsUploading={setIsUploading}
                              setError={setError}
                              editState={
                                formState === FORM_STATE_ENUM.EDIT_FORM
                              }
                              key="otherDocument"
                              id="otherDocument"
                              label="Upload Documents"
                              documentType={DOCUMENT_TITLES.otherDocument}
                              classNames="w-52 h-12"
                              existingDocument={existingDocuments.find(
                                (doc) =>
                                  doc.document_name ===
                                  DOCUMENT_TITLES.otherDocument,
                              )}
                            />
                          </div>
                        ) : (
                          <p className="w-52 h-12 text-black bg-gray-100 px-3 py-2 rounded">
                            {field.value || 'No Other Documents'}
                          </p>
                        )
                      ) : (
                        <div className="flex items-center justify-start">
                          <FileUpload
                            DOCUMENT_TITLES={DOCUMENT_TITLES}
                            MAX_FILE_SIZE={MAX_FILE_SIZE}
                            ACCEPTED_FILE_TYPES={ACCEPTED_FILE_TYPES}
                            existingDocuments={existingDocuments}
                            setExistingDocuments={setExistingDocuments}
                            documentStates={documentStates}
                            setDocumentStates={setDocumentStates}
                            uploadProgress={uploadProgress}
                            setUploadProgress={setUploadProgress}
                            isUploading={isUploading}
                            setIsUploading={setIsUploading}
                            setError={setError}
                            key="otherDocument"
                            id="otherDocument"
                            label="Upload Documents"
                            documentType={DOCUMENT_TITLES.otherDocument}
                            classNames="w-52 h-12"
                            existingDocument={existingDocuments.find(
                              (doc) =>
                                doc.document_name ===
                                DOCUMENT_TITLES.otherDocument,
                            )}
                          />
                        </div>
                      )}
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Additional Notes */}
            <div className="bg-gray-50 mx-6 p-4 rounded mt-12">
              <h2 className="text-base text-darkPurple bg-gray-100 rounded px-1 font-medium mb-7">
                Additional Notes
              </h2>

              <FormField
                control={form.control}
                name="additional_note"
                render={({ field }) => (
                  <FormItem className="col-span-2 group ease-in duration-300">
                    <FormLabel className="text-gray-600 group-hover:text-lightPurple">
                      Provide specific details about the part to ensure accurate
                      quotations.
                    </FormLabel>
                    <FormControl>
                      {formState === FORM_STATE_ENUM.DISPLAY_PART ||
                      formState === FORM_STATE_ENUM.DISPLAY_PART_RFQ ? (
                        <p className="w-full min-h-[80px] text-sm text-black bg-gray-100 px-3 py-2 rounded">
                          {field.value || '—'}
                        </p>
                      ) : (
                        <Textarea
                          {...field}
                          placeholder="Include any special requirements, tolerances, material preferences, or additional notes for the manufacturer."
                          className="focus:ring-0 focus:ring-offset-0 focus-visible:ring-0 focus-visible:ring-offset-0 focus-visible:outline-none focus:outline-none focus:border-lightPurple placeholder:text-gray-400 border border-gray-300 group-hover:border-lightPurple"
                        />
                      )}
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {error && (
              <div className="w-full flex items-center justify-center mt-4 ">
                <p className="bg-[#F8D7D7] text-[#B64444] text-sm py-3 px-3 rounded-lg">
                  {error}
                </p>
              </div>
            )}

            {/* Submit Button */}
            {formState !== FORM_STATE_ENUM.DISPLAY_PART &&
              formState !== FORM_STATE_ENUM.DISPLAY_PART_RFQ && (
                <div className="mt-4 w-full flex gap-4 items-center justify-center">
                  <Button
                    variant="outline"
                    className={`w-[100px] text-base text-darkPurple border-darkPurple transition ease-in-out duration-300 hover:text-darkPurple`}
                    onClick={(e) => {
                      e.preventDefault();
                      onClose();
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant="outline"
                    type="submit"
                    disabled={isLoading}
                    className={`w-[150px] text-base text-white hover:text-white transition ease-in-out duration-300 ${
                      isLoading
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-darkPurple hover:bg-darkerPurple'
                    } flex items-center justify-center`}
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      'Save'
                    )}
                  </Button>
                </div>
              )}
          </form>
        </Form>
      </div>
    </div>
  );
};

export default PartDetailsScreen2;
